import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus, Newspaper, Trash2, UploadCloud, Loader2, Edit2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { NewsItem } from '@/types';
import { Checkbox } from '../ui/checkbox';
import RichTextEditor from '../ui/RichTextEditor';

const BUCKET = 'news-images';
const CATEGORIES = ['Announcements', 'Updates', 'Features', 'Research', 'Community'];

const NewsManager: React.FC = () => {
  const { user } = useAuth();
  const [isAddNewsOpen, setIsAddNewsOpen] = useState(false);
  const [newNewsItem, setNewNewsItem] = useState<Omit<NewsItem, 'id' | 'date' | 'author' | 'likes'>>({
    title: '',
    content: '',
    image_url: '',
    category: CATEGORIES[0],
    is_featured: false,
    is_main_featured_article: false,
  });
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imageUploading, setImageUploading] = useState(false);
  const [newsItems, setNewsItems] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // --- Edit dialog state ---
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editNewsItem, setEditNewsItem] = useState<NewsItem | null>(null);
  const [editImageFile, setEditImageFile] = useState<File | null>(null);
  const [editImageUploading, setEditImageUploading] = useState(false);
  const editFileInputRef = useRef<HTMLInputElement>(null);

  // Fetch news from Supabase
  const fetchNews = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('news')
        .select('*')
        .order('date', { ascending: false });
      if (error) throw error;
      setNewsItems(data || []);
    } catch (err) {
      toast({
        title: "Error loading news",
        description: "Could not fetch news from the server.",
        variant: "destructive"
      });
      setNewsItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNews();
    // Listen for changes (realtime)
    const channel = supabase
      .channel('realtime-news')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'news' },
        () => fetchNews()
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
    // eslint-disable-next-line
  }, []);

  // Handle image upload to Supabase Storage
  const handleImageUpload = async (file: File): Promise<string | null> => {
    setImageUploading(true);
    try {
      const ext = file.name.split('.').pop()?.toLowerCase() || 'png';
      const safeFileName = `news-${Date.now()}.${ext}`.replace(/[^a-z0-9.\-_]/gi, '_');
  
      console.log("Uploading image:", {
        name: safeFileName,
        type: file.type,
        size: file.size,
      });
  
      const { data, error } = await supabase.storage
        .from(BUCKET)
        .upload(safeFileName, file, {
          upsert: true,
          contentType: file.type || 'image/png',
          cacheControl: '3600',
        });
  
      if (error) {
        console.error("Upload error:", error);
        throw error;
      }
  
      const { data: publicUrlData } = supabase.storage
        .from(BUCKET)
        .getPublicUrl(safeFileName);
  
      return publicUrlData?.publicUrl || null;
    } catch (err) {
      console.error("Image upload failed:", err);
      toast({
        title: "Image upload failed",
        description: "Could not upload image. Please try again.",
        variant: "destructive"
      });
      return null;
    } finally {
      setImageUploading(false);
    }
  };
  

  // Handle image upload for edit dialog
  const handleEditImageUpload = async (file: File): Promise<string | null> => {
    setEditImageUploading(true);
    try {
      const ext = file.name.split('.').pop();
      const fileName = `news-${Date.now()}.${ext}`;
      const { data, error } = await supabase.storage
        .from(BUCKET)
        .upload(fileName, file, { upsert: true, contentType: file.type });
      if (error) throw error;
      const { data: publicUrlData } = supabase.storage.from(BUCKET).getPublicUrl(fileName);
      return publicUrlData?.publicUrl || null;
    } catch (err) {
      toast({
        title: "Image upload failed",
        description: "Could not upload image. Please try again.",
        variant: "destructive"
      });
      return null;
    } finally {
      setEditImageUploading(false);
    }
  };

  const handleAddNews = async () => {
    if (!newNewsItem.title || !newNewsItem.content) {
      toast({
        title: "Missing information",
        description: "Please provide a title and content for the news item",
        variant: "destructive",
      });
      return;
    }
    let imageUrl = newNewsItem.image_url || '';
    if (imageFile) {
      const uploadedUrl = await handleImageUpload(imageFile);
      if (uploadedUrl) {
        imageUrl = uploadedUrl;
      } else {
        // If upload failed, abort
        return;
      }
    }
    try {
      const { error } = await supabase.from('news').insert({
        title: newNewsItem.title,
        content: newNewsItem.content,
        image_url: imageUrl || null,
        date: new Date().toISOString(),
        author: user?.user_metadata?.full_name || user?.email || 'Admin',
        category: newNewsItem.category || CATEGORIES[0],
        likes: 0, // Initialize likes to 0
        is_featured: newNewsItem.is_featured,
        is_main_featured_article: newNewsItem.is_main_featured_article,
      });
      if (error) throw error;
      setIsAddNewsOpen(false);
      setNewNewsItem({
        title: '',
        content: '',
        image_url: '',
        category: CATEGORIES[0],
        is_featured: false,
        is_main_featured_article: false,
      });
      setImageFile(null);
      toast({
        title: "News item added",
        description: "The news item has been published successfully",
      });
    } catch (err) {
      toast({
        title: "Error adding news",
        description: "Could not add news item.",
        variant: "destructive"
      });
    }
  };

  const handleDeleteNews = async (newsId: string) => {
    try {
      const { error } = await supabase.from('news').delete().eq('id', newsId);
      if (error) throw error;
      toast({
        title: "News item deleted",
        description: "The news item has been deleted successfully",
      });
    } catch (err) {
      toast({
        title: "Error deleting news",
        description: "Could not delete news item.",
        variant: "destructive"
      });
    }
  };

  // Handle file input change
  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      setNewNewsItem(prev => ({ ...prev, image_url: '' })); // Clear URL if uploading
    }
  };

  // Remove selected image
  const removeImage = () => {
    setImageFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // --- Edit dialog handlers ---
  const openEditDialog = (item: NewsItem) => {
    setEditNewsItem(item);
    setEditImageFile(null);
    setIsEditDialogOpen(true);
  };

  const handleEditFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setEditImageFile(file);
      setEditNewsItem(prev => prev ? { ...prev, image_url: '' } : prev);
    }
  };

  const removeEditImage = () => {
    setEditImageFile(null);
    if (editFileInputRef.current) editFileInputRef.current.value = '';
  };

  const handleEditNewsSave = async () => {
    if (!editNewsItem) return;
    if (!editNewsItem.title || !editNewsItem.content) {
      toast({
        title: "Missing information",
        description: "Please provide a title and content for the news item",
        variant: "destructive",
      });
      return;
    }
    let imageUrl = editNewsItem.image_url || '';
    if (editImageFile) {
      const uploadedUrl = await handleEditImageUpload(editImageFile);
      if (uploadedUrl) {
        imageUrl = uploadedUrl;
      } else {
        // If upload failed, abort
        return;
      }
    }
    try {
      const { error } = await supabase.from('news').update({
        title: editNewsItem.title,
        content: editNewsItem.content,
        image_url: imageUrl || null,
        category: editNewsItem.category || CATEGORIES[0],
        is_featured: editNewsItem.is_featured,
        is_main_featured_article: editNewsItem.is_main_featured_article,
      }).eq('id', editNewsItem.id);
      if (error) throw error;
      setIsEditDialogOpen(false);
      setEditNewsItem(null);
      setEditImageFile(null);
      toast({
        title: "News item updated",
        description: "The news item has been updated successfully",
      });
    } catch (err) {
      toast({
        title: "Error updating news",
        description: "Could not update news item.",
        variant: "destructive"
      });
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h3 className="text-xl font-semibold mb-2">News Management</h3>
          <p className="text-muted-foreground">
            Add and manage news items for your application
          </p>
        </div>
        <Button onClick={() => setIsAddNewsOpen(true)}>
          <Plus size={16} className="mr-2" />
          Add News Item
        </Button>
      </div>
      
      {loading ? (
        <div className="text-center py-10 border rounded-md bg-muted/30">
          <Newspaper className="mx-auto h-12 w-12 text-muted-foreground opacity-30" />
          <h3 className="mt-4 text-lg font-medium">Loading news...</h3>
        </div>
      ) : newsItems.length === 0 ? (
        <div className="text-center py-10 border rounded-md bg-muted/30">
          <Newspaper className="mx-auto h-12 w-12 text-muted-foreground opacity-30" />
          <h3 className="mt-4 text-lg font-medium">No news items</h3>
          <p className="mt-2 text-sm text-muted-foreground">
            Get started by adding your first news item.
          </p>
          <Button 
            onClick={() => setIsAddNewsOpen(true)} 
            variant="outline"
            className="mt-4"
          >
            <Plus size={16} className="mr-2" />
            Add News Item
          </Button>
        </div>
      ) : (
        <ScrollArea className="h-[400px]">
          <div className="grid grid-cols-1 gap-4">
            {newsItems.map((news) => (
              <Card key={news.id} className="overflow-hidden">
                <CardHeader className="pb-3">
                  <div className="flex justify-between items-start">
                    <CardTitle className="text-lg">{news.title}</CardTitle>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-muted-foreground"
                        onClick={() => openEditDialog(news)}
                        title="Edit"
                      >
                        <Edit2 size={16} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive"
                        onClick={() => handleDeleteNews(news.id)}
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-col md:flex-row gap-4">
                    {news.image_url && (
                      <div className="flex-shrink-0 md:w-1/3">
                        <img 
                          src={news.image_url} 
                          alt={news.title} 
                          className="aspect-video object-cover rounded-md w-full h-auto"
                        />
                      </div>
                    )}
                    <div className={news.image_url ? "md:w-2/3" : "w-full"}>
                      <div className="line-clamp-3 text-sm text-muted-foreground">
                        <div dangerouslySetInnerHTML={{ __html: news.content }} />
                      </div>
                    </div>
                  </div>
                </CardContent>
                <CardFooter className="text-xs text-muted-foreground border-t pt-3">
                  <div>
                    Posted by {news.author} on {news.date ? new Date(news.date).toLocaleDateString() : ''}
                  </div>
                </CardFooter>
              </Card>
            ))}
          </div>
        </ScrollArea>
      )}
      
      <Dialog open={isAddNewsOpen} onOpenChange={setIsAddNewsOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add News Item</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4 px-2">
            <div className="space-y-2">
              <Label htmlFor="newsTitle">Title</Label>
              <Input
                id="newsTitle"
                value={newNewsItem.title}
                onChange={e => setNewNewsItem({ ...newNewsItem, title: e.target.value })}
                placeholder="News title"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="newsContent">Content</Label>
              <RichTextEditor
                value={newNewsItem.content}
                onChange={content => setNewNewsItem({ ...newNewsItem, content })}
                placeholder="News content..."
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="newsCategory">Category</Label>
              <select
                id="newsCategory"
                value={newNewsItem.category || CATEGORIES[0]}
                onChange={e => setNewNewsItem({ ...newNewsItem, category: e.target.value })}
                className="w-full p-2 rounded-md border border-input bg-background"
              >
                {CATEGORIES.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="isFeatured"
                checked={newNewsItem.is_featured}
                onCheckedChange={checked => setNewNewsItem({ ...newNewsItem, is_featured: !!checked })}
              />
              <Label htmlFor="isFeatured" className="cursor-pointer text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                Feature this article on the Landing Page
              </Label>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="isMainFeatured"
                checked={!!newNewsItem.is_main_featured_article}
                onCheckedChange={checked => setNewNewsItem({ ...newNewsItem, is_main_featured_article: !!checked })}
              />
              <Label htmlFor="isMainFeatured" className="cursor-pointer text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                Large Feature on Landing Page
              </Label>
            </div>
            
            <div className="space-y-2">
              <Label>Image</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="file"
                  accept="image/*"
                  ref={fileInputRef}
                  onChange={onFileChange}
                  className="w-auto"
                  disabled={imageUploading}
                />
                {imageUploading && <Loader2 className="animate-spin h-5 w-5 text-muted-foreground" />}
                {imageFile && !imageUploading && (
                  <Button size="sm" variant="outline" onClick={removeImage}>
                    Remove
                  </Button>
                )}
              </div>
              {imageFile && (
                <div className="mt-2">
                  <div className="flex items-center gap-2">
                    <UploadCloud className="h-4 w-4 text-muted-foreground" />
                    <span className="text-xs">{imageFile.name}</span>
                  </div>
                  <img
                    src={URL.createObjectURL(imageFile)}
                    alt="Preview"
                    className="mt-2 rounded-md max-h-32"
                  />
                </div>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddNewsOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddNews} disabled={imageUploading}>
              {imageUploading ? <Loader2 className="animate-spin h-4 w-4 mr-2" /> : null}
              Publish News
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* --- Edit News Dialog --- */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit News Item</DialogTitle>
          </DialogHeader>
          {editNewsItem && (
            <div className="space-y-4 py-4 px-2">
              <div className="space-y-2">
                <Label htmlFor="editNewsTitle">Title</Label>
                <Input
                  id="editNewsTitle"
                  value={editNewsItem.title}
                  onChange={e => setEditNewsItem({ ...editNewsItem, title: e.target.value })}
                  placeholder="News title"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="editNewsContent">Content</Label>
                <RichTextEditor
                  value={editNewsItem.content}
                  onChange={content => setEditNewsItem({ ...editNewsItem, content })}
                  placeholder="News content..."
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="editNewsCategory">Category</Label>
                <select
                  id="editNewsCategory"
                  value={editNewsItem.category || CATEGORIES[0]}
                  onChange={e => setEditNewsItem({ ...editNewsItem, category: e.target.value })}
                  className="w-full p-2 rounded-md border border-input bg-background"
                >
                  {CATEGORIES.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="editIsFeatured"
                  checked={editNewsItem.is_featured}
                  onCheckedChange={checked => setEditNewsItem(prev => prev ? { ...prev, is_featured: !!checked } : null)}
                />
                <Label htmlFor="editIsFeatured" className="cursor-pointer text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                  Feature this article on the landing page
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="editIsMainFeatured"
                  checked={!!editNewsItem.is_main_featured_article}
                  onCheckedChange={checked => setEditNewsItem(prev => prev ? { ...prev, is_main_featured_article: !!checked } : null)}
                />
                <Label htmlFor="editIsMainFeatured" className="cursor-pointer text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                  Large Feature on Landing Page
                </Label>
              </div>
              <div className="space-y-2">
                <Label>Image</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="file"
                    accept="image/*"
                    ref={editFileInputRef}
                    onChange={handleEditFileChange}
                    className="w-auto"
                    disabled={editImageUploading}
                  />
                  {editImageUploading && <Loader2 className="animate-spin h-5 w-5 text-muted-foreground" />}
                  {editImageFile && !editImageUploading && (
                    <Button size="sm" variant="outline" onClick={removeEditImage}>
                      Remove
                    </Button>
                  )}
                </div>
                {(editImageFile || editNewsItem.image_url) && (
                  <div className="mt-2">
                    {editImageFile ? (
                      <>
                        <div className="flex items-center gap-2">
                          <UploadCloud className="h-4 w-4 text-muted-foreground" />
                          <span className="text-xs">{editImageFile.name}</span>
                        </div>
                        <img
                          src={URL.createObjectURL(editImageFile)}
                          alt="Preview"
                          className="mt-2 rounded-md max-h-32"
                        />
                      </>
                    ) : editNewsItem.image_url ? (
                      <img
                        src={editNewsItem.image_url}
                        alt="Current"
                        className="mt-2 rounded-md max-h-32"
                      />
                    ) : null}
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleEditNewsSave} disabled={editImageUploading}>
              {editImageUploading ? <Loader2 className="animate-spin h-4 w-4 mr-2" /> : null}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NewsManager;